import { Binary } from "./binary.js";
import { logger } from "./log.js";
const log = logger("Visibility");
export class Visibility {
    constructor(data) {
        for (const [key, value] of Object.entries(data))
            this[key] = value instanceof Uint8Array ? new Binary(value) : value;
        this.last = null;
        log.debug("initialized", { nodes: this.nodes.bytes.length / 32,
            leafStride: this.leafStride, visibilityBytes: this.vis.bytes.length });
    }
    leafAt(position) {
        if (!this.leafStride || !this.nodes.bytes.length) return -1;
        let node = this.rootNode ?? 0,
            steps = 0;
        while (node >= 0) {
            if (++steps > this.nodes.bytes.length / 32 + 1) return -1;
            const offset = node * 32,
                plane = this.nodes.i32(offset) * 20;
            node = this.nodes.i32(
                offset +
                    4 +
                    (this.planes.f32(plane) * position[0] + this.planes.f32(plane + 4) * position[1] + this.planes.f32(plane + 8) * position[2] >= this.planes.f32(plane + 12)
                        ? 0
                        : 4),
            );
        }
        return -node - 1;
    }
    contentsAt(position) {
        const leaf = this.leafAt(position);
        return leaf < 0 ? 0 : this.leaves.i32(leaf * this.leafStride);
    }
    areaAt(position) {
        const leaf = this.leafAt(position);
        return leaf < 0 ? -1 : this.leaves.u16(leaf * this.leafStride + 6) & 0x1ff;
    }
    facesInArea(area) {
        if (area < 0 || !this.leafStride) return new Set();
        const faces = new Set();
        for (let offset = 0; offset < this.leaves.bytes.length; offset += this.leafStride) {
            if ((this.leaves.u16(offset + 6) & 0x1ff) !== area) continue;
            const first = this.leaves.u16(offset + 20), count = this.leaves.u16(offset + 22);
            for (let i = 0; i < count; i++) faces.add(this.leafFaces.u16((first + i) * 2));
        }
        return faces;
    }
    ambientCubeAt(position) {
        try {
            let leaf = this.leafAt(position);
            if (leaf < 0) return null;
            const decode = (reader, start) => Array.from({ length: 6 }, (_, face) => {
                // VRAD stores ambient cubes in linear 0..1 units, unlike the
                // 0..255 surface lightmaps. RGBExp therefore needs no /255 here.
                const o = start + face * 4, scale = 2 ** reader.i8(o + 3);
                return [0, 1, 2].map(c => reader.u8(o + c) * scale);
            });
            if (!this.ambientIndex?.bytes.length)
                return this.leafStride === 56 ? decode(this.leaves, leaf * 56 + 30) : null;
            const visited = new Set();
            let count, first;
            while (!(count = this.ambientIndex.u16(leaf * 4))) {
                // Empty leaves refer to a neighboring leaf's ambient samples.
                // This also supplies light to props whose origins are in solids.
                if (visited.has(leaf)) return null;
                visited.add(leaf);
                leaf = this.ambientIndex.u16(leaf * 4 + 2);
            }
            first = this.ambientIndex.u16(leaf * 4 + 2);
            const mins = [0, 1, 2].map((i) =>
                this.leaves.i16(leaf * this.leafStride + 8 + i * 2),
            );
            const maxs = [0, 1, 2].map((i) =>
                this.leaves.i16(leaf * this.leafStride + 14 + i * 2),
            );
            const cube = Array.from({ length: 6 }, () => [0, 0, 0]);
            let weightSum = 0;
            for (let i = first; i < first + count; i++) {
                const distance = mins.reduce(
                    (sum, min, axis) =>
                        sum +
                        (min +
                            ((maxs[axis] - min) *
                                this.ambient.u8(i * 28 + 24 + axis)) /
                                255 -
                            position[axis]) **
                            2,
                    0,
                );
                const weight = 1 / (distance + 1), sample = decode(this.ambient, i * 28);
                weightSum += weight;
                for (let face = 0; face < 6; face++)
                    for (let c = 0; c < 3; c++) cube[face][c] += sample[face][c] * weight;
            }
            return cube.map(color => color.map(c => c / weightSum));
        } catch (error) {
            if (!this.warnedAmbient) {
                log.error("ambient lookup failed; using fallback", error);
                this.warnedAmbient = true;
            }
            return null;
        }
    }
    ambientAt(position) {
        const cube = this.ambientCubeAt(position);
        return cube ? [0, 1, 2].map(c => cube.reduce((sum, face) => sum + face[c], 0) / 6) : [1, 1, 1];
    }
    trace(start, end) {
        if (!this.nodes.bytes.length || !this.brushes?.bytes.length) return { fraction: 1, sky: true };
        let fraction = 1, sky = false, hitNormal, material;
        const visited = new Set(), stack = [this.rootNode ?? 0];
        let steps = 0;
        while (stack.length) {
            if (++steps > this.nodes.bytes.length / 16 + this.leaves.bytes.length / this.leafStride + 1) {
                if (!this.warnedTrace) { log.warn("invalid BSP trace tree", { steps }); this.warnedTrace = true; }
                return { fraction: 1, sky: false };
            }
            const node = stack.pop();
            if (node >= 0) {
                const offset = node * 32, p = this.nodes.i32(offset) * 20, nx = this.planes.f32(p), ny = this.planes.f32(p + 4), nz = this.planes.f32(p + 8), d = this.planes.f32(p + 12);
                const a = -d + nx * start[0] + ny * start[1] + nz * start[2], b = -d + nx * end[0] + ny * end[1] + nz * end[2];
                if (a >= 0 || b >= 0) stack.push(this.nodes.i32(offset + 4));
                if (a <= 0 || b <= 0) stack.push(this.nodes.i32(offset + 8));
                continue;
            }
            const leaf = (-node - 1) * this.leafStride, first = this.leaves.u16(leaf + 24), count = this.leaves.u16(leaf + 26);
            for (let i = 0; i < count; i++) {
                const id = this.leafBrushes.u16((first + i) * 2);
                if (visited.has(id)) continue; visited.add(id);
                const brush = id * 12, sides = this.brushes.i32(brush + 4);
                if (!(this.brushes.i32(brush + 8) & 1)) continue;
                let enter = -Infinity, leave = 1, entrySide = -1, miss = false;
                for (let side = 0; side < sides; side++) {
                    const offset = (this.brushes.i32(brush) + side) * 8, p = this.brushSides.u16(offset) * 20,
                        nx = this.planes.f32(p), ny = this.planes.f32(p + 4), nz = this.planes.f32(p + 8), d = this.planes.f32(p + 12);
                    const a = -d + nx * start[0] + ny * start[1] + nz * start[2], b = -d + nx * end[0] + ny * end[1] + nz * end[2];
                    if (a > 0 && b > 0) { miss = true; break; }
                    if (a <= 0 && b <= 0) continue;
                    const t = a / (a - b);
                    if (a > b && t > enter) { enter = t; entrySide = offset; }
                    else if (a < b) leave = Math.min(leave, t);
                }
                // Ignore the containing brush when a model's lighting origin
                // lies inside geometry; Source likewise relocates bad samples.
                if (miss || enter < 0 || enter >= leave || enter >= fraction) continue;
                fraction = enter;
                const texinfo = this.brushSides.i16(entrySide + 2);
                sky = texinfo >= 0 && !!(this.texinfo.i32(texinfo * 72 + 64) & 6);
                hitNormal = this.planes.vec(this.brushSides.u16(entrySide) * 20);
                const data = texinfo >= 0 ? this.texinfo.i32(texinfo * 72 + 68) : -1;
                material = data >= 0 && this.texdata ? this.materialNames?.[data] : undefined;
            }
        }
        return { fraction, sky, normal: hitNormal, material };
    }
    // Cluster membership is also used for terrain, submodels and static props.
    clustersForLeaves(leaves) {
        const clusters = new Set();
        for (const leaf of leaves) {
            const cluster = this.leaves.i16(leaf * this.leafStride + 4);
            if (cluster >= 0) clusters.add(cluster);
        }
        return [...clusters].sort((a, b) => a - b);
    }
    boundsForLeaves(leaves) {
        if (!this.leafStride || !leaves.length) return null;
        const mins = [Infinity, Infinity, Infinity], maxs = [-Infinity, -Infinity, -Infinity];
        for (const leaf of leaves) {
            const offset = leaf * this.leafStride;
            for (let axis = 0; axis < 3; axis++) {
                mins[axis] = Math.min(mins[axis], this.leaves.i16(offset + 8 + axis * 2));
                maxs[axis] = Math.max(maxs[axis], this.leaves.i16(offset + 14 + axis * 2));
            }
        }
        return { mins, maxs };
    }
    clustersForFace(face) {
        if (!this.faceClusters) {
            this.faceClusters = new Map();
            if (this.leafStride) for (let o = 0; o < this.leaves.bytes.length; o += this.leafStride) {
                const cluster = this.leaves.i16(o + 4);
                if (cluster < 0) continue;
                const first = this.leaves.u16(o + 20), count = this.leaves.u16(o + 22);
                for (let i = 0; i < count; i++) {
                    const id = this.leafFaces.u16((first + i) * 2);
                    if (!this.faceClusters.has(id)) this.faceClusters.set(id, new Set());
                    this.faceClusters.get(id).add(cluster);
                }
            }
        }
        return [...(this.faceClusters.get(face) ?? [])].sort((a, b) => a - b);
    }
    clustersForBounds(mins, maxs) {
        if (!this.leafStride || !this.nodes.bytes.length) return [];
        const stack = [this.rootNode ?? 0], leaves = [], visited = new Set();
        while (stack.length) {
            const node = stack.pop();
            if (visited.has(node)) continue;
            visited.add(node);
            if (node < 0) { leaves.push(-node - 1); continue; }
            const o = node * 32, p = this.nodes.i32(o) * 20, normal = this.planes.vec(p), distance = this.planes.f32(p + 12);
            let low = -distance, high = -distance;
            for (let axis = 0; axis < 3; axis++) {
                low += normal[axis] * (normal[axis] >= 0 ? mins[axis] : maxs[axis]);
                high += normal[axis] * (normal[axis] >= 0 ? maxs[axis] : mins[axis]);
            }
            // mathlib.h BoxOnPlaneSide2 at zero tolerance: the leading
            // corner includes the plane; the trailing corner is strictly back.
            if (high >= 0) stack.push(this.nodes.i32(o + 4));
            if (low < 0) stack.push(this.nodes.i32(o + 8));
        }
        return this.clustersForLeaves(leaves);
    }
    visibleClusters(position) {
        if (!this.leafStride || !this.nodes.bytes.length || !this.vis.bytes.length) return null;
        try {
            const leaf = this.leafAt(position);
            if (leaf < 0) return null;
            const cluster = this.leaves.i16(leaf * this.leafStride + 4);
            if (cluster < 0) return null;
            if (cluster === this.last) return this.clusters;
            const count = this.vis.i32(0);
            if (cluster >= count || count <= 0 || count > 1_000_000) return null;
            let offset = this.vis.i32(4 + cluster * 8);
            if (offset < 4 + count * 8) return null;
            const bits = new Uint8Array(Math.ceil(count / 8));
            let index = 0;
            while (index < bits.length) {
                const b = this.vis.u8(offset++);
                if (b) bits[index++] = b;
                else {
                    const run = this.vis.u8(offset++);
                    if (!run) return null;
                    // bsplib.cpp DecompressVis clamps a final zero run to the
                    // PVS row size, (numclusters + 7) >> 3.
                    index += Math.min(run, bits.length - index);
                }
            }
            const clusters = new Set();
            for (let c = 0; c < count; c++) if (bits[c >> 3] & (1 << (c & 7))) clusters.add(c);
            this.last = cluster; this.clusters = clusters; this.faces = null;
            return clusters;
        } catch (error) {
            if (!this.warnedPvs) { log.error("PVS lookup failed; using frustum culling", error); this.warnedPvs = true; }
            return null;
        }
    }
    visibleFaces(position) {
        const clusters = this.visibleClusters(position);
        if (!clusters) return null;
        if (!this.faces) {
            this.faces = new Set();
            for (let o = 0; o < this.leaves.bytes.length; o += this.leafStride) {
                if (!clusters.has(this.leaves.i16(o + 4))) continue;
                const first = this.leaves.u16(o + 20), count = this.leaves.u16(o + 22);
                for (let i = 0; i < count; i++) this.faces.add(this.leafFaces.u16((first + i) * 2));
            }
        }
        return this.faces;
    }
}
